import {
  keepPreviousData,
  type QueryKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import type {
  PaginatedSmsConversations,
  SmsConversationDetail,
  SmsConversationListItem,
  SmsMessage,
  SmsMessagesPage,
} from '../../types/messaging';
import { api, apiEndpoints } from '../../utils/apiService';
import { type ListFilter, type Me, VIEW_FILTERS, type View } from './smsModel';

export const LIST_PAGE_SIZE = 25;
export const POLL_MS = 30_000;
const API_MAX_PAGE_SIZE = 100;
const EARLIER_PAGE_SIZE = 50;

async function readError(response: Response | null, fallback: string) {
  if (!response) return fallback;
  const body = (await response.json().catch(() => null)) as {
    detail?: string;
  } | null;
  return body?.detail || fallback;
}

async function getJson<T>(url: string, fallback: string): Promise<T> {
  const response = await api.get(url);
  if (!response?.ok) throw new Error(await readError(response, fallback));
  return response.json();
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface ListParams {
  view: View;
  unread: boolean;
  search: string;
  limit: number;
}

export interface ListResult {
  rows: SmsConversationListItem[];
  total: number;
}

async function fetchSource(
  filter: ListFilter,
  { unread, search, limit }: Omit<ListParams, 'view'>,
): Promise<ListResult> {
  const rows: SmsConversationListItem[] = [];
  const pageSize = Math.min(limit, API_MAX_PAGE_SIZE);
  let total = 0;
  for (let page = 1; ; page++) {
    const data = await getJson<PaginatedSmsConversations>(
      apiEndpoints.smsConversations({
        ...filter,
        unread: unread || undefined,
        search: search || undefined,
        page,
        page_size: pageSize,
        ordering: '-last_message_at',
      }),
      'Conversations could not be loaded.',
    );
    rows.push(...(data.results ?? []));
    total = data.count ?? rows.length;
    if (!data.next || rows.length >= limit) break;
  }
  return { rows, total };
}

const byLatest = (a: SmsConversationListItem, b: SmsConversationListItem) =>
  (b.last_message_at ?? '').localeCompare(a.last_message_at ?? '');

/**
 * The newest `limit` conversations of a view. A view spanning two statuses
 * fetches `limit` from each and keeps the newest `limit` of the union, which
 * is exact for the rows shown.
 */
export async function fetchConversations(
  params: ListParams,
): Promise<ListResult> {
  const sources = await Promise.all(
    VIEW_FILTERS[params.view].map((f) => fetchSource(f, params)),
  );
  return {
    rows: sources
      .flatMap((s) => s.rows)
      .sort(byLatest)
      .slice(0, params.limit),
    total: sources.reduce((n, s) => n + s.total, 0),
  };
}

async function fetchCount(filter: ListFilter, search: string): Promise<number> {
  const data = await getJson<PaginatedSmsConversations>(
    apiEndpoints.smsConversations({
      ...filter,
      search: search || undefined,
      page: 1,
      page_size: 1,
    }),
    'Counts could not be loaded.',
  );
  return data.count ?? 0;
}

export type ViewCounts = Record<View, number>;

/**
 * Segment counts. Every conversation that isn't archived is OPEN, PENDING or
 * CLOSED, so All is their sum; Archived is counted on its own.
 */
export async function fetchCounts(search: string): Promise<ViewCounts> {
  const live = { archived: false };
  const [open, pending, closed, needsOpen, needsPending, archived] =
    await Promise.all([
      fetchCount({ status: 'OPEN', ...live }, search),
      fetchCount({ status: 'PENDING', ...live }, search),
      fetchCount({ status: 'CLOSED', ...live }, search),
      fetchCount({ status: 'OPEN', needs_human: true, ...live }, search),
      fetchCount({ status: 'PENDING', needs_human: true, ...live }, search),
      fetchCount({ archived: true }, search),
    ]);
  return {
    inbox: open + pending,
    needs: needsOpen + needsPending,
    closed,
    all: open + pending + closed,
    archived,
  };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

function useSmsKeys() {
  const root = useScopedQueryKey('sms');
  const nav = useScopedQueryKey('nav-unread-sms');
  return useMemo(
    () => ({
      root,
      nav,
      lists: [...root, 'list'] as QueryKey,
      counts: [...root, 'counts'] as QueryKey,
      detail: (id: number) => [...root, 'conversation', id] as QueryKey,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(root), JSON.stringify(nav)],
  );
}

export function useConversationList(params: ListParams) {
  const keys = useSmsKeys();
  return useQuery({
    queryKey: [
      ...keys.lists,
      params.view,
      params.unread,
      params.search,
      params.limit,
    ],
    queryFn: () => fetchConversations(params),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });
}

export function useViewCounts(search: string) {
  const keys = useSmsKeys();
  return useQuery({
    queryKey: [...keys.counts, search],
    queryFn: () => fetchCounts(search),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });
}

export function useConversation(id: number | null) {
  const keys = useSmsKeys();
  return useQuery({
    queryKey: keys.detail(id ?? 0),
    queryFn: () =>
      getJson<SmsConversationDetail>(
        apiEndpoints.smsConversationDetail(id as number),
        'This conversation could not be loaded.',
      ),
    enabled: id != null,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });
}

export async function fetchEarlier(
  id: number,
  before: number,
): Promise<SmsMessagesPage> {
  return getJson<SmsMessagesPage>(
    apiEndpoints.smsConversationMessages(id, {
      before,
      limit: EARLIER_PAGE_SIZE,
    }),
    'Earlier messages could not be loaded.',
  );
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export type ConversationPatch = Partial<{
  assigned_to: number | null;
  needs_human: boolean;
  status: 'OPEN' | 'CLOSED';
  is_closed: boolean;
  archived: boolean;
}>;

function pendingMessage(body: string, me: Me | null | undefined): SmsMessage {
  const now = new Date().toISOString();
  return {
    id: -Date.now(),
    direction: 'OUTBOUND',
    sender_phone_e164: '',
    recipient_phone_e164: '',
    body,
    provider: '',
    provider_message_sid: '',
    provider_status: '',
    status: 'QUEUED',
    error_code: '',
    error_message: '',
    sent_by_user: me?.id ?? null,
    sent_by_user_email: me?.email ?? null,
    sent_by_agent: false,
    message_type: 'TEXT',
    num_segments: 1,
    price: null,
    created_at: now,
    updated_at: now,
    delivered_at: null,
    failed_at: null,
  };
}

export function useSmsActions() {
  const queryClient = useQueryClient();
  const keys = useSmsKeys();

  const refreshLists = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.lists }),
        queryClient.invalidateQueries({ queryKey: keys.counts }),
        queryClient.invalidateQueries({ queryKey: keys.nav }),
      ]),
    [queryClient, keys],
  );

  const patchRows = useCallback(
    (id: number, patch: Partial<SmsConversationListItem>) => {
      queryClient.setQueriesData<ListResult>(
        { queryKey: keys.lists },
        (current) =>
          current && {
            ...current,
            rows: current.rows.map((r) =>
              r.id === id ? { ...r, ...patch } : r,
            ),
          },
      );
      queryClient.setQueryData<SmsConversationDetail>(
        keys.detail(id),
        (current) => current && { ...current, ...patch },
      );
    },
    [queryClient, keys],
  );

  /** POST …/{id}/mark-read/ */
  const markRead = useCallback(
    async (id: number) => {
      const response = await api.post(apiEndpoints.smsConversationMarkRead(id));
      if (!response?.ok) return;
      await queryClient.cancelQueries({ queryKey: keys.detail(id) });
      patchRows(id, { unread_count: 0 });
      await queryClient.invalidateQueries({ queryKey: keys.nav });
    },
    [queryClient, keys, patchRows],
  );

  /** POST …/{id}/mark-unread/ */
  const markUnread = useCallback(
    async (id: number) => {
      const response = await api.post(
        apiEndpoints.smsConversationMarkUnread(id),
      );
      if (!response?.ok) {
        throw new Error(
          await readError(response, 'Could not mark this as unread.'),
        );
      }
      await queryClient.cancelQueries({ queryKey: keys.detail(id) });
      patchRows(id, { unread_count: 1 });
      await queryClient.invalidateQueries({ queryKey: keys.nav });
    },
    [queryClient, keys, patchRows],
  );

  /** PATCH …/{id}/update/ — take over, hand back, close, reopen, archive. */
  const update = useCallback(
    async (id: number, payload: ConversationPatch) => {
      const response = await api.patch(
        apiEndpoints.smsConversationUpdate(id),
        payload,
      );
      if (!response?.ok) {
        throw new Error(
          await readError(response, 'The conversation could not be updated.'),
        );
      }
      const updated = (await response.json()) as SmsConversationDetail;
      // A detail fetch started before the write would land stale on top.
      await queryClient.cancelQueries({ queryKey: keys.detail(id) });
      queryClient.setQueryData(keys.detail(id), updated);
      patchRows(id, updated);
      await refreshLists();
    },
    [queryClient, keys, patchRows, refreshLists],
  );

  /**
   * POST …/{id}/send/ { body }. The turn shows as Sending until the refetch
   * brings the stored message; on failure it is removed and the caller keeps
   * the draft.
   */
  const send = useCallback(
    async (id: number, body: string, me: Me | null | undefined) => {
      const key = keys.detail(id);
      const pending = pendingMessage(body, me);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<SmsConversationDetail>(
        key,
        (current) =>
          current && { ...current, messages: [...current.messages, pending] },
      );
      const dropPending = () =>
        queryClient.setQueryData<SmsConversationDetail>(
          key,
          (current) =>
            current && {
              ...current,
              messages: current.messages.filter((m) => m.id !== pending.id),
            },
        );
      let response: Response | null;
      try {
        response = await api.post(apiEndpoints.smsConversationSend(id), {
          body,
        });
      } catch (e) {
        dropPending();
        throw e;
      }
      if (!response?.ok) {
        dropPending();
        throw new Error(
          await readError(response, 'The message could not be sent.'),
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        refreshLists(),
      ]);
    },
    [queryClient, keys, refreshLists],
  );

  /** POST …/mark-all-read/ — every unread conversation for this store. */
  const markAllRead = useCallback(async () => {
    const response = await api.post(apiEndpoints.smsConversationMarkAllRead());
    if (!response?.ok) {
      throw new Error(await readError(response, 'Could not mark all as read.'));
    }
    await refreshLists();
    await queryClient.invalidateQueries({
      queryKey: keys.detail(0).slice(0, -1),
    });
  }, [queryClient, keys, refreshLists]);

  return { markRead, markUnread, update, send, markAllRead };
}
